import {
    AddType, BlurEvent, Button, ChangeEvent, ClickEvent, DataSource, DataSourceAction, DataSourceExecutionEvent,
    DataSourceMode,
    DataSourceModeChangeEvent, DropdownSelectionEvent, LookupModelSearchEvent,
    LookupModelSelectionEvent, Panel, TableRow, Textbox
} from "@mcleod/components";
import { Api, Currency, CurrencySettings, CurrencyUtil, DisplayType, ModelRow } from "@mcleod/core";
import { getDispatchControlBoolean } from "@mcleod/general";
import { RateUtil } from "./RateUtil";
import { TaxUtil } from "./TaxUtil";
import { getExciseTaxControlBoolean } from "../../ar/src/models/ModelExciseTaxControl";
import { AutogenLayoutRecOrderRates } from "./autogen/AutogenLayoutRecOrderRates";
import { OtherChargesUtil } from "./OtherChargesUtil";
import { CommonDialogs } from "@mcleod/common";
import { ModelCustomer } from "@mcleod/ar";

export class RecOrderRates extends AutogenLayoutRecOrderRates {

    private _allocationEnabled: boolean;
    private _calculatingCharges = false;
    private chargePanelExpanded: boolean = true;
    private estimateFuelSurcharge: boolean = getDispatchControlBoolean("est_fuel_surcharge")
    private autoRate: boolean = getDispatchControlBoolean("auto_rate");
    private otherChargesUtil: OtherChargesUtil = new OtherChargesUtil();
    private taxUtil: TaxUtil = new TaxUtil();
    private taxPanelExpanded: boolean = true;
    doAfterRating: () => void;
    checkPickupAndDeliveryEntered = () => true;

    override onLoad(): void {
        this.textboxCollectionMethod.items = RateUtil.getCollectionMethodDropdownItems();
        this.textboxCtrlParty.textCombined.manualAddLayout = null;
        this.buttonEstimateFuelSurcharge.visible = !this.estimateFuelSurcharge;
        this.taxUtil.initialize(this);
        this.otherChargesUtil.initialize(this);
        this.textboxCurrencyType.visible = CurrencySettings.getSingleton().multiCurrencyEnabled();
        if (!CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            this.textboxCurrencyType.defaultDataValue = CurrencySettings.getFunctionalCurrency();
        } else {
            this.textboxCurrencyType.defaultDataValue = CurrencySettings.getTransactionalCurrency();
            this.updateCurrencySelector(this.textboxCurrencyType.defaultDataValue);
        }
    }

    /** This is an event handler for the afterExecution event of sourceRecurringOrder.  */
    sourceRecurringOrderAfterExecution(event: DataSourceExecutionEvent) {
        RateUtil.setAutoRateContext(this, this.mainDataSource.activeRow.get("rate_id"), this.mainDataSource.activeRow.get("orig_dest_rate_id"));
        RateUtil.enableDisableUnitsAndRates(this, this.activeRow.get("rate_type"), event.getAction() != DataSourceAction.SEARCH, false);
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            if (this.mainDataSource.mode !== DataSourceMode.ADD) {
                const transactionalCurrency = this.getTransactionalCurrency();
                const orderCurrencyCode = this.mainDataSource.activeRow?.get("freight_charge_c", transactionalCurrency) as string;
                this.mainDataSource.activeRow?.setLookupModelData("freight_charge_c", { currency_type_id: orderCurrencyCode });
                this.mainDataSource.displayDataInBoundComponent(this.textboxCurrencyType);
                this.otherChargesUtil.currencyType = orderCurrencyCode;
                this.taxUtil.currencyType = orderCurrencyCode;
            }
        }
    }

    /** This is an event handler for the afterModeChange event of sourceRecurringOrder.  */
    sourceRecurringOrderAfterModeChange(event: DataSourceModeChangeEvent) {
        if ((event.newMode == DataSourceMode.ADD || event.newMode == DataSourceMode.UPDATE) && event.newMode != event.oldMode) {
            this.sourceOrderOtherChargeRec.mode = DataSourceMode.ADD;
            this.checkSegAllocEnabled();
            if (CurrencySettings.getSingleton().multiCurrencyEnabled() && getExciseTaxControlBoolean("is_active", false)) {
                this.setTaxesVisible(true);
            } else {
                this.setTaxesVisible(false);
            }
        }
        RateUtil.setRateListeners(this);
        if (event.newMode === DataSourceMode.ADD || event.newMode === DataSourceMode.UPDATE) {
            CurrencyUtil.configureCurrencySelector(this.textboxCurrencyType);
        }
    }

    set calculatingCharges(value: boolean) {
        this._calculatingCharges = value;
    }

    get calculatingCharges(): boolean {
        return this._calculatingCharges;
    }

    textboxSegAllocCodeBeforeLookupModelSearch(event) {
        event.filter.alloc_type = 'M';
        event.filter.is_active = 'Y';
    }

    buttonExpandChargesOnClick(event: ClickEvent) {
        const transitionProps = { speed: 100, paddingTop: 0 };
        if (this.chargePanelExpanded) this.panelOtherChargeTable.collapse(transitionProps).then(() => {
            this.buttonExpandCharges.imageRotation = 90
        }); else this.panelOtherChargeTable.expand(transitionProps).then(() => {
            this.buttonExpandCharges.imageRotation = 0
        });
        this.chargePanelExpanded = !this.chargePanelExpanded;
    }

    private checkSegAllocEnabled() {
        this._allocationEnabled = this.sourceRecurringOrder.activeRow?.get("seg_alloc_enabled");
        delete this.sourceRecurringOrder.activeRow?.data["seg_alloc_enabled"];
        this.textboxSegAllocCode.visible = this._allocationEnabled;
        // May need to make this required in a future card
        //this.textboxSegAllocCode.required = this._allocationEnabled;
        if (!this._allocationEnabled) this.textboxSegAllocCode.dataSource = null;
    }

    async textboxChargeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            const selectedRow = event.selection;
            const textboxDescr = TableRow.getContainingTableRow(event.target)?.findComponentById("textboxDescr") as Textbox;
            if (textboxDescr != null)
                textboxDescr.text = selectedRow.get("descr");
            await this.chargeIdChanged(event.target);
        }
    }

    getOtherChargeDatasource(): DataSource {
        return this.sourceOrderOtherChargeRec;
    }

    async otherChargeOnBlur(event: BlurEvent) {
        await this.otherChargesUtil.otherChargeOnBlur(event);
    }

    buttonEstimateFuelSurchargeOnClick(event: ClickEvent) {
        this.ratingButtonClicked(event, false, true);
    }

    buttonRateOnClick(event: ClickEvent) {
        this.ratingButtonClicked(event, true);
    }

    ratingButtonClicked(event: ClickEvent, autoRate: boolean, estimateFsc: boolean = this.estimateFuelSurcharge) {
        const button = event.target as Button;
        button.busy = true;
        this.calculateRates(autoRate, estimateFsc).finally(() => {
            button.busy = false
        });
    }

    async chargeIdChanged(textbox: Textbox) {
        await this.otherChargesUtil.chargeIdChanged(textbox);
    }

    async tableOtherChargesOnContentsChanged() {
        await this.otherChargesUtil.tableOtherChargesOnContentsChanged();
        this.mainDataSource.activeRow.set("totalcharge_and_excisetax", this.taxUtil.getTaxChargeBillTotal(true));
    }

    async calculateRates(autorate: boolean = this.autoRate, estimateFsc: boolean = this.estimateFuelSurcharge): Promise<any> {
        if (!this.mainDataSource.isAddingOrUpdating() || this.calculatingCharges || this.mainDataSource.activeRow == null || !this.checkPickupAndDeliveryEntered()) return Promise.resolve();
        this.mainDataSource.setComponentsBusy(true);
        this.calculatingCharges = true;
        const orderRow: ModelRow = this.mainDataSource.getDataboundValues(null, true, null, null);
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            if (this.otherChargesUtil.currencyType && this.activeRow?.getCurrency("freight_charge").getCurrencyCode() !==
                this.otherChargesUtil.currencyType) {
                this.convertPricingCurrencies(this.otherChargesUtil.currencyType);
            }
            orderRow.set("freight_charge", this.activeRow.get("freight_charge"));
        }
        const response = await Api.search("lme/dispatch/calculate-order-charges", {
            "order_type": "R",
            "autorate": autorate,
            "estimate_fsc": estimateFsc,
            "order_row": orderRow,
            "calculate_max_target": false
        });
        let totalChargeChanged = false;
        const data = response.data[0];
        const orderApiData = data["order_row"] || [];

        // Estimate Fuel Surcharge
        if (estimateFsc) {
            const appendingId: string[] = [];
            this.tableOtherCharges.rows.forEach(row => {
                if (row.data?._appending == false) appendingId.push(row.data?._data["id"])
            });
            for (let i = this.tableOtherCharges.rows.length - 1; i >= 0; i--) {
                const row = this.tableOtherCharges.rows[i];
                if (row?.data?.data["est_fuel_surcharge"] === "Y") {
                    row.deleteRow()
                }
            }
            this.sourceOrderOtherChargeRec.data = [];
            const otherCharges = data["other_charges"] || [];
            otherCharges.forEach(element => {
                const isAppending = appendingId.length == 0 || !appendingId.includes(element["id"]);
                const modelRow = new ModelRow(this.sourceOrderOtherChargeRec.url, isAppending);
                modelRow.setValues(element);
                this.sourceOrderOtherChargeRec.data.push(modelRow);
            });
            this.tableOtherCharges.displayData(null, this.sourceOrderOtherChargeRec.data, 0);
        }

        // Auto Rate
        this.textboxRateType.setSelectedItemFromValue(orderApiData["rate_type"]);
        this.mainDataSource.activeRow.set("bill_distance", orderApiData["bill_distance"]);
        this.mainDataSource.activeRow.set("autorate_status", orderApiData["autorate_status"]);
        this.mainDataSource.activeRow.set("rate_id", orderApiData["rate_id"]);
        this.mainDataSource.activeRow.set("rate", orderApiData["rate"]);
        this.mainDataSource.activeRow.data["calc_method"] = data["rate_type"];
        this.mainDataSource.activeRow.set("rate_units", orderApiData["rate_units"]);
        this.mainDataSource.activeRow.set("freight_charge", orderApiData["freight_charge"]);
        this.mainDataSource.activeRow.set("otherchargetotal", await this.otherChargesUtil.getOtherChargeTotal(false));
        if (orderApiData["total_charge"] != null) {
            if (orderApiData["total_charge"].amount != this.mainDataSource.activeRow.get("total_charge")?.amount || 0) {
                this.mainDataSource.activeRow.set("total_charge", orderApiData["total_charge"]);
                this.mainDataSource.activeRow.set("total_charge_n", orderApiData["total_charge_n"]);
                totalChargeChanged = true;
            }
        }


        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            const exciseTaxes = data["excise_taxes"] || [];
            const orderCurrency = this.mainDataSource.activeRow?.get("freight_charge_c", await this.getTransactionalCurrency()) as string;
            await this.convertPricingCurrencies(orderCurrency);
        }

        const excisetaxTotal = this.taxUtil.getTaxChargeBillTotal(false);
        this.mainDataSource.activeRow.set("excisetax_total", excisetaxTotal);
        const totalCharge: Currency = this.mainDataSource.activeRow.getCurrency("total_charge") ?? new Currency(0);
        this.mainDataSource.activeRow.set("totalcharge_and_excisetax", new Currency({
            amount: (totalCharge.amount ?? 0) + (excisetaxTotal.amount ?? 0),
            currency_code: excisetaxTotal.currency_code,
            conversion_date: excisetaxTotal.conversion_date
        }));

        RateUtil.setAutoRateContext(this, this.mainDataSource.activeRow.get("rate_id"), this.mainDataSource.activeRow.get("orig_dest_rate_id"));
        if (totalChargeChanged && this.doAfterRating != null) this.doAfterRating();

        this.calculatingCharges = false;
        this.mainDataSource.setComponentsBusy(false);
    }

    // Not used?  Was previously referenced by another method that was never called...
    private makeRateIdTooltip(rateId) {
        const tooltipPanel: Panel = new Panel({ width: 150 });
        const textboxRateId = new Textbox();
        textboxRateId.printable = true;
        textboxRateId.caption = "Rate ID";
        textboxRateId.text = rateId;
        textboxRateId.displayType = DisplayType.STRING;
        tooltipPanel.add(textboxRateId);
        return tooltipPanel;
    }

    /** This is an event handler for the onChange event of checkboxLockMiles.  */
    checkboxLockMilesOnChange(event) {
        this.textboxBillDistance.enabled = event.newValue === false;
    }

    textboxCurrencyTypeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const selectedRow = event.selection;
        if (selectedRow) {
            const currencyType = selectedRow.get("currency_type_id", this.getTransactionalCurrency()) as string;
            this.convertPricingCurrencies(currencyType);
        }
    }

    getTransactionalCurrency(): string {
        return CurrencySettings.getTransactionalCurrency();
    }

    async convertPricingCurrencies(currencyType: string) {
        this.otherChargesUtil.currencyType = currencyType;
        this.taxUtil.currencyType = currencyType;
        const freightCharge: Currency = this.mainDataSource.activeRow?.getCurrency("freight_charge");
        freightCharge?.setCurrencyCode(currencyType);
        freightCharge?.setConversionDate(freightCharge.getConversionDate());
        const otherCharge: Currency = this.mainDataSource.activeRow?.getCurrency("otherchargetotal");
        otherCharge?.setCurrencyCode(currencyType);
        const totalCharge: Currency = this.mainDataSource.activeRow?.getCurrency("total_charge");
        totalCharge?.setCurrencyCode(currencyType);
        const totalTaxes: Currency = this.mainDataSource.activeRow?.getCurrency("excisetax_total");
        totalTaxes?.setCurrencyCode(currencyType);
        const totalFreightChargeWithTaxes: Currency = this.mainDataSource.activeRow?.getCurrency("totalcharge_and_excisetax");
        totalFreightChargeWithTaxes?.setCurrencyCode(currencyType);
        this.mainDataSource.activeRow?.setValues({
            "freight_charge": freightCharge,
            "otherchargetotal": otherCharge,
            "total_charge": totalCharge,
            "excisetax_total": totalTaxes,
            "totalcharge_and_excisetax": totalFreightChargeWithTaxes
        });
        this.updateCurrencySelector(currencyType);
        for (const tableRow of this.tableOtherCharges.rows) {
            const modelRow: ModelRow = tableRow.data;
            const amount: Currency = modelRow.getCurrency("amount");
            if (amount?.getAmount() != null) {
                if (modelRow.isNull("customer_id")) {
                    amount.setCurrencyCode(currencyType);
                    modelRow.updateCurrency("amount", amount);
                    tableRow.displayComponentValues();
                    modelRow.setValueAndLookupModelData("amount_c", currencyType, { currency_type_id: currencyType });
                }
            }
        }

        const quickAddData: ModelRow = this.tableOtherCharges.quickAddData;
        if (quickAddData) {
            const amount: Currency = quickAddData.getCurrency("amount");
            if (amount?.getAmount() != null) {
                if (quickAddData.isNull("customer_id")) {
                    amount.setCurrencyCode(currencyType);
                }
                quickAddData.updateCurrency("amount", amount);
                this.tableOtherCharges.quickAddRow.displayComponentValues();
                quickAddData.setValueAndLookupModelData("amount_c", currencyType, { currency_type_id: currencyType });
            }
        }
        for (const tableRow of this.tableTax.rows) {
            const modelRow: ModelRow = tableRow.data;
            const amount: Currency = modelRow.getCurrency("amount");
            if (amount?.getAmount() != null) {
                amount.setCurrencyCode(currencyType);
                modelRow.updateCurrency("amount", amount);
                tableRow.displayComponentValues();
            }
        }
        const quickAddDataTax: ModelRow = this.tableTax.quickAddData;
        if (quickAddDataTax) {
            const amount: Currency = quickAddDataTax.getCurrency("amount");
            if (amount?.getAmount() != null) {
                amount.setCurrencyCode(currencyType);
                quickAddDataTax.set("amount", amount);
                this.tableTax.quickAddRow.displayComponentValues();
            }
        }
    }

    private updateCurrencySelector(currencyCode: string) {
        this.mainDataSource.activeRow?.set("freight_charge_c", currencyCode);
        this.mainDataSource.activeRow?.setLookupModelData("freight_charge_c", { currency_type_id: currencyCode });
        this.mainDataSource.displayDataInBoundComponent(this.textboxCurrencyType);
    }

    private setTaxesVisible(visible: boolean) {
        this.panelRecOrderExciseTaxTotal.visible = visible;
        this.panelRecOrderTotalWithTaxes.visible = visible;
        this.panelTaxes.visible = visible;
        if (!visible) {
            this.tableOtherCharges.removeColumn("columnHeaderTaxable");
        }
        this.setExciseTaxableOptions();
    }

    getTaxDatasource(): DataSource {
        return this.sourceExcisetaxChargeRec;
    }

    public getTaxTable() {
        return this.tableTax;
    }

    async switchTaxableOrderOnChange(event: ChangeEvent) {
        if (this.activeRow == null || this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        const isChecked = event?.newValue === true;
        const taxRows = this.tableTax.rows;
        if (!isChecked && taxRows.length > 0) {
            const message = "This change will result in non-taxable orders and will remove all excise taxes currently defined for this recurring order.\n\nProceed with tax status change for this recurring order?";
            const confirm = event.userInitiatedChange ? await CommonDialogs.showYesNo(message) : true;
            if (confirm) {
                for (let i = taxRows.length - 1; i >= 0; i--) {
                    const row = taxRows[i];
                    this.tableTax.deleteRow(row);
                }

                // Recalculate totals and refresh bound controls
                this.taxUtil.tableTaxOnContentsChanged();
                this.mainDataSource.displayDataInBoundComponent(this.textboxRecOrderExciseTaxTotal);
                this.mainDataSource.displayDataInBoundComponent(this.textboxRecOrderTotalWithTax);
                this.setExciseTaxableOptions();
            } else {
                // Revert switch back to ON (taxable) and refresh control display
                this.switchTaxableOrder.checked = true;
            }
        } else {
            // Turning ON simply enables tax editing options
            this.setExciseTaxableOptions();
        }
    }

    private setExciseTaxableOptions() {
        const isExciseTaxable: boolean = this.activeRow.getBoolean("excise_taxable", false);
        this.tableTax.allowEdit = isExciseTaxable;
        this.tableTax.addType = isExciseTaxable ? AddType.QUICK : AddType.NOT_ALLOWED;
        this.tableTax.allowDelete = isExciseTaxable;
        this.tableTax.displayToolsCell = isExciseTaxable;
        this.sourceExcisetaxChargeRec.displayDataInBoundComponents();
    }

    async switchExciseDisableUpdateOnChange(event: ChangeEvent) {
        if (this.activeRow == null || this.mainDataSource.mode === DataSourceMode.SEARCH) return;

        const isChecked = event?.newValue === true;
        if (isChecked) {
            const message = "This change will result in disabling the auto update and creation of tax records in Order Entry and when creating Invoices.\n\nProceed with disabling tax auto update?";
            const confirm = event.userInitiatedChange ? await CommonDialogs.showYesNo(message) : true;
            if (confirm) {
                // Recalculate and update totals
                this.taxUtil.tableTaxOnContentsChanged();
                this.mainDataSource.displayDataInBoundComponent(this.textboxRecOrderExciseTaxTotal);
                this.mainDataSource.displayDataInBoundComponent(this.textboxRecOrderTotalWithTax);
                this.setExciseTaxableOptions();
            } else {
                // Revert checkbox
                this.switchExciseDisableUpdate.checked = false;
            }
        } else {
            // Update button state when switch is toggled off or there are no tax rows
            this.setExciseTaxableOptions();
        }
    }

    buttonExpandTaxesOnClick(event: ClickEvent) {
        const transitionProps = { speed: 100, paddingTop: 0 };
        if (this.taxPanelExpanded) {
            this.panelTaxTable.collapse(transitionProps).then(() => {
                this.buttonExpandTaxes.imageRotation = 90
            });
        } else {
            this.panelTaxTable.expand(transitionProps).then(() => {
                this.buttonExpandTaxes.imageRotation = 0
            });
        }
        this.taxPanelExpanded = !this.taxPanelExpanded;
    }

    tableTaxOnContentsChanged(event) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
            this.taxUtil.tableTaxOnContentsChanged();
    }

    async textboxExcisetaxIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        if (event.hasSelection() === true) {
            await this.taxUtil.textboxExciseTaxIdOnLookupModelSelection(event);
        }
    }

    async taxesOnBlur(event: BlurEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        await this.taxUtil.taxOnBlur(event);
    }

    async taxMethodAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        await this.taxUtil.taxOnDropdownSelection(event);

        // Enable/disable Units and Rate fields based on Method value
        const selectedMethod = event.selection?.value;
        const tableRow = TableRow.getContainingTableRow(event.target);
        this.updateTaxFieldsEnabledState(tableRow, selectedMethod);
    }

    tableTaxOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow() as TableRow;

        const modelRow: ModelRow = tableRow.data as ModelRow;
        const method = modelRow.get("method");

        // Enable/disable Units and Rate fields based on Method value
        this.updateTaxFieldsEnabledState(tableRow, method);
    }

    private updateTaxFieldsEnabledState(tableRow: TableRow, method: string) {
        if (tableRow) {
            const textboxUnits = tableRow.findComponentById("textboxUnits") as Textbox;
            if (textboxUnits) {
                textboxUnits.required = method === "F";
            }
        }
    }

    textboxStateIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.country_id = "in CAN,CAD"; // show only Canadian provinces
    }
}
